import axios from 'src/axios';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { APICONSTANTS } from 'src/components/Constants';

const isKeyMissing = (key, lang) => {
  const resources = i18n.getResource(lang, 'translation');
  // console.log(resources);
  return !resources[key];
};

const addKey = (key, translation, lang) => {
  i18n.addResourceBundle(lang, 'translation', {
    [key]: translation
  });

  i18n.changeLanguage(lang);
};

// 'true'  = queued/in-flight, will not be re-queued
// deleted = available to be queued again
const translationCache = {};

// Pending batch: lang -> Set of texts waiting to be sent
const pendingBatch = {};
const batchTimers = {};

const flushBatch = async (lang) => {
  const texts = [...(pendingBatch[lang] || new Set())];
  delete pendingBatch[lang];
  delete batchTimers[lang];

  if (texts.length === 0) return;

  try {
    const response = await axios.post(APICONSTANTS.updateTranslation, {
      texts,
      lang
    });

    const translationMap = response.data;
    texts.forEach((text) => {
      const translation = translationMap[text];
      if (translation) {
        addKey(String(text), translation, lang);
        // Successfully translated — allow re-queue on next load if needed
        delete translationCache[text];
      }
      // No translation returned (rate limited etc.) — leave in cache so
      // this string is NOT re-queued endlessly in this session.
      // It will be retried on the next page load once the DB has warmed up.
    });
  } catch (err) {
    // On network/server error, clear cache after a 30s cooldown so strings
    // can be retried — but not immediately, which would cause a tight loop.
    setTimeout(() => {
      texts.forEach((text) => delete translationCache[text]);
    }, 30000);
    console.error('Translation error occurred');
  }
};

const queueTranslation = (text, currentLanguage) => {
  if (!pendingBatch[currentLanguage]) pendingBatch[currentLanguage] = new Set();
  pendingBatch[currentLanguage].add(text);

  // Mark as queued so t() won't add it again before the batch fires
  translationCache[text] = true;

  // Debounce: collect keys for 50ms then send as one batch request
  if (batchTimers[currentLanguage]) clearTimeout(batchTimers[currentLanguage]);
  batchTimers[currentLanguage] = setTimeout(
    () => flushBatch(currentLanguage),
    50
  );
};

// Custom hook for translation
export const useAutoTranslation = () => {
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  useEffect(() => {
    setCurrentLanguage(i18n.language);
  }, [i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps

  const { t: tx } = useTranslation();

  const t = (key) => {
    if (!key) return ''; // Prevent empty keys

    if (currentLanguage === 'en') return tx(key);
    key = String(key).replace(/\./g, '_');
    if (isKeyMissing(key, currentLanguage) && !translationCache[key]) {
      queueTranslation(key, currentLanguage);
    }

    return tx(key).replace(/_/g, '.');
  };

  return t;
};
